import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface BatteryCycle {
  id: string;
  userId: string;
  username: string;
  telegramUserId: string;
  status: string;
  health: number;
  cycleEarnings: number;
  todayEarnings: number;
  purchasePrice: number;
  purchaseMemo: string | null;
  totalCharges: number;
  activeDays: number;
  perfectDays: number;
  isBurnedOut: boolean;
  cycleStartedAt: string;
  cycleEndsAt: string;
  lastChargeAt: string | null;
  earningsWithdrawn: boolean;
}

type FilterStatus = 'all' | 'active' | 'pending_payment' | 'burned' | 'awaiting_withdrawal' | 'completed' | 'expired';

export function AdminBatteries() {
  const [batteries, setBatteries] = useState<BatteryCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');

  const fetchBatteries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminBatteries(filter);
      setBatteries(res.data.batteries || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchBatteries(); }, [fetchBatteries]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'pending_payment': return '#f59e0b';
      case 'burned': return '#ef4444';
      case 'awaiting_withdrawal': return '#f97316';
      case 'completed': return '#3b82f6';
      case 'expired': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'pending_payment': return 'Pending';
      case 'burned': return 'Burned';
      case 'awaiting_withdrawal': return 'Withdrawal';
      case 'completed': return 'Done';
      case 'expired': return 'Expired';
      default: return status;
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}, ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const activeCount = batteries.filter(b => b.status === 'active').length;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', padding: '16px' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
        {(['all', 'active', 'pending_payment', 'burned', 'completed', 'expired'] as FilterStatus[]).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '7px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
              border: filter === s ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
              background: filter === s ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)',
              color: filter === s ? '#60a5fa' : '#9ca3af',
            }}
          >
            {s === 'all' ? `All (${batteries.length})` :
             s === 'active' ? `Active (${activeCount})` :
             getStatusLabel(s)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : batteries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔋</div>
          <div>No batteries found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {batteries.map(b => (
            <div key={b.id} style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px', padding: '14px',
            }}>
              {/* Header: user + status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🔋</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{b.username}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>ID: {b.telegramUserId}</div>
                  </div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                  background: `${getStatusColor(b.status)}20`, color: getStatusColor(b.status),
                  border: `1px solid ${getStatusColor(b.status)}40`,
                }}>
                  {getStatusLabel(b.status)}
                </span>
              </div>

              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
                <div style={statBox}>
                  <div style={statLabel}>Health</div>
                  <div style={{ ...statValue, color: b.health >= 50 ? '#10b981' : b.health > 0 ? '#f59e0b' : '#ef4444' }}>{b.health}%</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Earnings</div>
                  <div style={statValue}>{b.cycleEarnings.toLocaleString()}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Charges</div>
                  <div style={statValue}>{b.totalCharges}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Active Days</div>
                  <div style={statValue}>{b.activeDays}/30</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Perfect Days</div>
                  <div style={statValue}>{b.perfectDays}</div>
                </div>
                <div style={statBox}>
                  <div style={statLabel}>Price</div>
                  <div style={statValue}>{b.purchasePrice} USDT</div>
                </div>
              </div>

              {/* Dates */}
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <div>Started: {formatDate(b.cycleStartedAt)}</div>
                <div>Ends: {formatDate(b.cycleEndsAt)}</div>
                {b.lastChargeAt && <div>Last charge: {formatDate(b.lastChargeAt)}</div>}
                {b.purchaseMemo && <div style={{ marginTop: '4px', color: '#9ca3af' }}>Memo: {b.purchaseMemo}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const statBox: React.CSSProperties = {
  background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '8px', textAlign: 'center',
};
const statLabel: React.CSSProperties = { fontSize: '10px', color: '#6b7280', marginBottom: '2px' };
const statValue: React.CSSProperties = { fontSize: '14px', fontWeight: 700, color: '#fff' };
